import { FeatureToggle, isFeatureEnabled } from '../util/featureToggle';
import { GoogleAnalyticsIdManager } from '../modules/app/GoogleAnalyticsIdManager';
import { getGoogleAnalyticsClientId, getGoogleAnalyticsSessionId } from '../util/google';
import { TrackingTimeout } from './GoogleAnalyticsEventsBase';

declare global {
    interface Window {
        dataTrackerUrl: string | undefined;
    }
}

export type BigQueryEventParamsType = {
    eventKey: string;
    eventValue: string;
};

export type BigQueryDataType = {
    eventName: string;
    eventParams: BigQueryEventParamsType[];
};

type BigQueryRequestDataType = BigQueryDataType & {
    locale: string;
    pageLocation: string;
    sessionId: string;
    clientId: string;
};

export class BigQuery {
    #timeout: number | null = null;

    public async track(data: BigQueryDataType): Promise<void> {
        if (isFeatureEnabled(FeatureToggle.GOOGLE_TAG_MANAGER)) {
            return;
        }

        await this.#send(data);
    }

    public trackWithDelay(
        data: BigQueryDataType,
        delay: number = TrackingTimeout.SEARCH_DELAY
    ): void {
        if (this.#timeout !== null) {
            window.clearTimeout(this.#timeout);
        }

        this.#timeout = window.setTimeout(async (): Promise<void> => {
            await this.track(data);
        }, delay);
    }

    async #send(data: BigQueryDataType): Promise<void> {
        const { dataTrackerUrl } = window;
        if (!dataTrackerUrl) {
            return;
        }

        try {
            const requestData: BigQueryRequestDataType = await this.#formatRequestData(data);
            const parsedData = this.#parseData(requestData);

            if (!('sendBeacon' in window.navigator)) {
                await this.#useFetch(dataTrackerUrl, parsedData);
            }

            const blob = new Blob([parsedData], { type: 'text/plain' });
            const succeeded = window.navigator.sendBeacon(dataTrackerUrl, blob);

            if (succeeded) {
                return;
            }

            await this.#useFetch(dataTrackerUrl, parsedData);
        } catch (e) {
            // stub
        }
    }

    #parseData(data: BigQueryRequestDataType): string {
        const encoder = new TextEncoder();
        const utf8Bytes = encoder.encode(JSON.stringify(data));
        return btoa(String.fromCharCode(...utf8Bytes));
    }

    async #useFetch(dataTrackerUrl: string, parsedData: string): Promise<void> {
        await fetch(dataTrackerUrl, {
            body: parsedData,
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' }
        });
    }

    async #formatRequestData(data: BigQueryDataType): Promise<BigQueryRequestDataType> {
        const clientId =
            (await getGoogleAnalyticsClientId()) ?? GoogleAnalyticsIdManager.UNKNOWN_VALUE;
        const sessionId =
            (await getGoogleAnalyticsSessionId()) ?? GoogleAnalyticsIdManager.UNKNOWN_VALUE;

        return {
            locale: document.documentElement.dataset.locale ?? '',
            pageLocation: window.location.href,
            sessionId: sessionId,
            clientId: clientId,
            eventName: data.eventName,
            eventParams: data.eventParams.map(({ eventKey, eventValue }) => ({
                eventKey: eventKey,
                eventValue: eventValue
            }))
        };
    }
}

export default new BigQuery();
